import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { TagInput } from './TagInput';
import { Upload, X, CheckCircle, AlertCircle, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatBytes } from '@/lib/utils';

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface FileUpload {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  url?: string;
  error?: string;
}

const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB chunks
const CHUNKED_THRESHOLD = 100 * 1024 * 1024; // Use chunking for files > 100MB

export function UploadDialog({ open, onOpenChange, onSuccess }: UploadDialogProps) {
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Upload options
  const [vanityUrl, setVanityUrl] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [expiresIn, setExpiresIn] = useState<number | undefined>(undefined);
  const [selectedTags, setSelectedTags] = useState<Array<{ id: string; name: string }>>([]);
  const [availableTags, setAvailableTags] = useState<Array<{ id: string; name: string }>>([]);

  // Load available tags
  useEffect(() => {
    if (open) {
      api
        .getTags()
        .then((response) => setAvailableTags(response.tags))
        .catch(() => setAvailableTags([]));
    }
  }, [open]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles: FileUpload[] = acceptedFiles.map((file) => ({
      file,
      status: 'pending',
      progress: 0,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isUploading,
  });

  const uploadFileChunked = async (fileUpload: FileUpload, index: number) => {
    const { file } = fileUpload;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    try {
      // Initialize upload
      const initResponse = await fetch('/api/chunked-upload/init', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${api.getToken()}`,
        },
        body: JSON.stringify({
          filename: file.name,
          mimeType: file.type,
          totalSize: file.size,
          totalChunks,
        }),
      });

      if (!initResponse.ok) {
        const error = await initResponse.json();
        throw new Error(error.message || 'Failed to initialize upload');
      }

      const { uploadId } = await initResponse.json();

      // Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('uploadId', uploadId);
        formData.append('chunkIndex', i.toString());

        const chunkResponse = await fetch('/api/chunked-upload/chunk', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${api.getToken()}`,
          },
          body: formData,
        });

        if (!chunkResponse.ok) {
          throw new Error('Failed to upload chunk');
        }

        // Update progress
        const progress = Math.round(((i + 1) / totalChunks) * 90);
        setFiles((prev) => {
          const updated = [...prev];
          const current = updated[index];
          if (current) {
            updated[index] = { ...current, progress };
          }
          return updated;
        });
      }

      // Complete upload
      const completeResponse = await fetch('/api/chunked-upload/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${api.getToken()}`,
        },
        body: JSON.stringify({ uploadId }),
      });

      if (!completeResponse.ok) {
        const error = await completeResponse.json();
        throw new Error(error.message || 'Failed to complete upload');
      }

      const data = await completeResponse.json();

      setFiles((prev) => {
        const updated = [...prev];
        const current = updated[index];
        if (current) {
          updated[index] = {
            ...current,
            status: 'success',
            progress: 100,
            url: data.url,
          };
        }
        return updated;
      });
    } catch (error: unknown) {
      const err = error as { message?: string };
      setFiles((prev) => {
        const updated = [...prev];
        const current = updated[index];
        if (current) {
          updated[index] = {
            ...current,
            status: 'error',
            progress: 0,
            error: err.message || 'Upload failed',
          };
        }
        return updated;
      });
    }
  };

  const uploadFile = async (fileUpload: FileUpload, index: number) => {
    // Use chunked upload for large files
    if (fileUpload.file.size > CHUNKED_THRESHOLD) {
      return uploadFileChunked(fileUpload, index);
    }

    // Standard upload for smaller files
    const formData = new FormData();
    formData.append('file', fileUpload.file);

    // Add upload options
    if (vanityUrl.trim()) formData.append('vanityUrl', vanityUrl.trim());
    formData.append('isPublic', isPublic.toString());
    if (expiresIn) formData.append('expiresIn', expiresIn.toString());

    try {
      setFiles((prev) => {
        const updated = [...prev];
        const current = updated[index];
        if (current) {
          updated[index] = { ...current, status: 'uploading', progress: 50 };
        }
        return updated;
      });

      const response = await fetch('/api/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${api.getToken()}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      setFiles((prev) => {
        const updated = [...prev];
        const current = updated[index];
        if (current) {
          updated[index] = {
            ...current,
            status: 'success',
            progress: 100,
            url: data.url,
          };
        }
        return updated;
      });
    } catch (error: unknown) {
      const err = error as { message?: string };
      setFiles((prev) => {
        const updated = [...prev];
        const current = updated[index];
        if (current) {
          updated[index] = {
            ...current,
            status: 'error',
            progress: 0,
            error: err.message || 'Upload failed',
          };
        }
        return updated;
      });
    }
  };

  const handleUpload = async () => {
    setIsUploading(true);

    try {
      // Upload files sequentially
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file && file.status === 'pending') {
          await uploadFile(file, i);
        }
      }

      const successCount = files.filter((f) => f.status === 'success').length;
      const errorCount = files.filter((f) => f.status === 'error').length;

      if (successCount > 0) {
        toast.success(`${successCount} file(s) uploaded successfully`);
        onSuccess();
      }

      if (errorCount > 0) {
        toast.error(`${errorCount} file(s) failed to upload`);
      }

      // Close dialog if all uploads successful
      if (errorCount === 0) {
        setTimeout(() => {
          onOpenChange(false);
          setFiles([]);
        }, 1000);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      setVanityUrl('');
      setIsPublic(true);
      setExpiresIn(undefined);
      setSelectedTags([]);
      setShowAdvanced(false);
      onOpenChange(false);
    }
  };

  const handleAddTag = async (tagId: string) => {
    const tag = availableTags.find((t) => t.id === tagId);
    if (tag && !selectedTags.find((t) => t.id === tagId)) {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    setSelectedTags(selectedTags.filter((t) => t.id !== tagId));
  };

  const handleCreateTag = async (name: string) => {
    try {
      const response = await api.createTag(name);
      const newTag = response.tag;
      setAvailableTags([...availableTags, newTag]);
      setSelectedTags([...selectedTags, newTag]);
      toast.success(`Tag "${name}" created`);
    } catch (error: unknown) {
      const err = error as { message?: string };
      toast.error(err.message || 'Failed to create tag');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Files</DialogTitle>
          <DialogDescription>
            Drag and drop files here or click to browse
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            {isDragActive ? (
              <p className="text-lg font-medium">Drop files here...</p>
            ) : (
              <>
                <p className="text-lg font-medium mb-2">Drop files here or click to browse</p>
                <p className="text-sm text-muted-foreground">
                  Support for images, videos, documents, and more
                </p>
              </>
            )}
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Files ({files.length})</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {files.map((fileUpload, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card"
                  >
                    {fileUpload.status === 'success' && (
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    )}
                    {fileUpload.status === 'error' && (
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    )}
                    {fileUpload.status === 'uploading' && (
                      <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    )}
                    {fileUpload.status === 'pending' && (
                      <Upload className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}

                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{fileUpload.file.name}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground">
                          {formatBytes(fileUpload.file.size)}
                          {fileUpload.file.size > CHUNKED_THRESHOLD && (
                            <span className="text-primary ml-2">• Chunked</span>
                          )}
                        </p>
                        {fileUpload.status === 'uploading' && (
                          <div className="flex-1 max-w-[120px]">
                            <div className="w-full bg-muted rounded-full h-1.5">
                              <div
                                className="bg-primary h-1.5 rounded-full transition-all"
                                style={{ width: `${fileUpload.progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {fileUpload.error && (
                        <p className="text-xs text-red-500 mt-1">{fileUpload.error}</p>
                      )}
                    </div>

                    {fileUpload.status === 'pending' && !isUploading && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Advanced Options */}
          <div className="space-y-4 pt-4 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full"
            >
              <Settings2 className="h-4 w-4 mr-2" />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </Button>

            {showAdvanced && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                {/* Vanity URL */}
                <div className="space-y-2">
                  <Label htmlFor="vanityUrl">Custom Short URL (Optional)</Label>
                  <Input
                    id="vanityUrl"
                    placeholder="my-custom-url"
                    value={vanityUrl}
                    onChange={(e) => setVanityUrl(e.target.value)}
                    disabled={isUploading}
                  />
                  <p className="text-xs text-muted-foreground">
                    3-50 characters: letters, numbers, hyphens, underscores
                  </p>
                </div>

                {/* Public/Private Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="isPublic">Public Upload</Label>
                    <p className="text-xs text-muted-foreground">
                      {isPublic ? 'Anyone can view this file' : 'Only you can view this file'}
                    </p>
                  </div>
                  <Switch
                    id="isPublic"
                    checked={isPublic}
                    onCheckedChange={setIsPublic}
                    disabled={isUploading}
                  />
                </div>

                {/* Expiration */}
                <div className="space-y-2">
                  <Label htmlFor="expiresIn">Expire After (Optional)</Label>
                  <select
                    id="expiresIn"
                    value={expiresIn || ''}
                    onChange={(e) => setExpiresIn(e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={isUploading}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">Never</option>
                    <option value="1">1 hour</option>
                    <option value="6">6 hours</option>
                    <option value="24">24 hours</option>
                    <option value="168">7 days</option>
                    <option value="720">30 days</option>
                  </select>
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <Label>Tags (Optional)</Label>
                  <TagInput
                    tags={selectedTags}
                    availableTags={availableTags}
                    onAddTag={handleAddTag}
                    onRemoveTag={handleRemoveTag}
                    onCreateTag={handleCreateTag}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={handleClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={files.length === 0 || isUploading}
            >
              {isUploading ? 'Uploading...' : `Upload ${files.length} file(s)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
